import { ActorRef, assign, createMachine, DoneInvokeEvent, forwardTo, send, spawn } from "xstate";
import { ScheduleStatus } from "@iventis/domain-model/model/scheduleStatus";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { AxiosResponse } from "axios";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { TwoWayObject } from "@iventis/utilities";
import { PermissionContext, permissionsEditMachine } from "@iventis/permissions/src/permissions-edit.machine";

import { scheduleApi } from "@iventis/api/src/api";
import { Content } from "@iventis/translations";
import { translate } from "@iventis/translations/translation";
import {
    CopyVersionsMachineContext,
    copyScheduleVersionsMachine,
    CopyVersionsMachineEvents,
    NodeWithExpanded,
    SelectVersionToCopyEvent,
    COPY_MACHINE_ID,
} from "./copy-schedule-versions.machine";
import { CreateScheduleResponseType } from "./schedule.types";

export type CreateScheduleContext = {
    schedule: ScheduleVersion;
    validationError: { name: string };
    existingScheduleVersions: NodeWithExpanded[];
    permissions: PermissionContext;
    // This is a reference to the copy Schedule Versions machine and is used after we spawn it to read from it's context and send messages to it
    copyRef: ActorRef<CopyVersionsMachineEvents, { context: CopyVersionsMachineContext }>;
    isPermissionsIdle: boolean;
};

export type UpdateNameEvent = { type: "UPDATE_NAME"; payload: string };

export type UpdateStatusEvent = { type: "UPDATE_STATUS"; payload: ScheduleStatus };

export type UpdateVersionCopyIdsEvent = { type: "UPDATE_VERSION_COPY_IDS"; payload: { existingScheduleVersion: Node; selectedIds: string[] } };

export type UpdatePermissions = { type: "UPDATE_PERMISSIONS"; payload: PermissionContext };

export type UpdatePermissionsIdleStateEvent = {
    type: "UPDATE_PERMISSIONS_IDLE_STATE";
    payload: boolean;
};

export type EventWithNoPayload<T> = { type: T };

export type CreateScheduleEvent = EventWithNoPayload<"CREATE">;

export type CloseScheduleEvent = EventWithNoPayload<"CLOSE">;

export type SaveScheduleEvent = EventWithNoPayload<"SAVE">;

export type CreateScheduleEvents =
    | UpdateNameEvent
    | UpdateStatusEvent
    | CreateScheduleEvent
    | CloseScheduleEvent
    | SaveScheduleEvent
    | UpdateVersionCopyIdsEvent
    | UpdatePermissions
    | SelectVersionToCopyEvent
    | CopyVersionsMachineEvents
    | UpdatePermissionsIdleStateEvent;

const isNameValid = (name: string) => typeof name === "string" && name.length !== 0;

const isValid = (context, event) => {
    switch (event.type) {
        case "UPDATE_NAME": {
            return isNameValid(event.payload);
        }
        default: {
            return isNameValid(context.schedule.name);
        }
    }
};

const actions = {
    updateName: assign({
        schedule: (context: CreateScheduleContext, event: UpdateNameEvent) => ({ ...context.schedule, name: event.payload }),
        validationError: (context: CreateScheduleContext, event: UpdateNameEvent) => ({
            ...context.validationError,
            name: isNameValid(event.payload) ? null : "Name must not be empty",
        }),
    }),
    updateStatus: assign({ schedule: (context: CreateScheduleContext, event: UpdateStatusEvent) => ({ ...context.schedule, status: event.payload }) }),
};

export const createScheduleMachine = (onScheduleCreated: (context: CreateScheduleContext) => void) =>
    createMachine<CreateScheduleContext, CreateScheduleEvents>(
        {
            id: "Schedule",
            initial: "createStep",
            context: {
                schedule: { name: "", status: ScheduleStatus.Draft, dataFieldValues: [], type: null, id: null, parentId: null, scheduleId: null, publishedAt: null },
                existingScheduleVersions: [],
                validationError: { name: null },
                permissions: {
                    originalPermissions: null,
                    latestPermissions: null,
                    localToRemoteMapping: new TwoWayObject<string, string>({}, {}),
                    permissionSelector: null,
                    errorMessage: null,
                    node: null,
                    close: false,
                    canLock: false,
                    isChild: true,
                    isNodeCreated: false,
                },
                isPermissionsIdle: false,
                copyRef: null,
            },
            states: {
                createStep: {
                    id: "create",
                    initial: "loading",
                    states: {
                        loading: {
                            entry: [
                                assign({
                                    copyRef: ({ existingScheduleVersions }) =>
                                        spawn(
                                            copyScheduleVersionsMachine.withContext({ piblingSchedules: existingScheduleVersions, scheduleVersionIdsToCopy: [] }),
                                            COPY_MACHINE_ID
                                        ), // Spawn copy machine with initial context as the existing schedule versions
                                }),
                            ],
                            always: "idle",
                        },
                        idle: {
                            initial: "invalid",
                            on: {
                                UPDATE_NAME: [
                                    { cond: isValid, target: ".valid", actions: actions.updateName },
                                    { target: ".invalid", actions: actions.updateName },
                                ],
                                UPDATE_STATUS: [
                                    { cond: isValid, target: ".valid", actions: actions.updateStatus },
                                    { target: ".invalid", actions: actions.updateStatus },
                                ],
                                EXPAND_OR_COLLAPSE: {
                                    actions: forwardTo((context) => context.copyRef),
                                },
                                SELECT_OR_DESELECT_NODE: {
                                    actions: forwardTo((context) => context.copyRef),
                                },
                            },
                            states: {
                                valid: {
                                    entry: assign({ validationError: () => ({ name: null }) }),
                                    id: "valid",
                                    on: {
                                        CREATE: "#creating",
                                    },
                                },
                                invalid: {
                                    id: "invalid",
                                },
                            },
                        },
                        creating: {
                            id: "creating",
                            invoke: {
                                src: "createSchedule",
                                onDone: {
                                    target: "#permissions",
                                    actions: [
                                        assign({
                                            permissions: (context, event: DoneInvokeEvent<CreateScheduleResponseType>) => ({ ...context.permissions, node: event.data.node }),
                                            schedule: (_, event: DoneInvokeEvent<CreateScheduleResponseType>) => event.data.schedule,
                                        }),
                                        (context) => onScheduleCreated(context),
                                    ],
                                },
                                onError: "failure",
                            },
                        },
                        failure: {},
                    },
                },
                permissionsStep: {
                    id: "permissions",
                    invoke: {
                        id: "permissions",
                        src: permissionsEditMachine,
                        onDone: "finished",
                        data: (context) => context.permissions,
                    },
                    on: {
                        UPDATE_PERMISSIONS: {
                            actions: assign({ permissions: (_, event) => event.payload }),
                        },
                        UPDATE_PERMISSIONS_IDLE_STATE: {
                            actions: assign({ isPermissionsIdle: (_, event) => event.payload }),
                        },
                        SAVE: {
                            actions: send({ type: "SAVE" }, { to: "permissions" }),
                        },
                        CLOSE: {
                            actions: send({ type: "CLOSE" }, { to: "permissions" }),
                        },
                    },
                },
                finished: {
                    type: "final",
                },
            },
        },
        {
            services: {
                createSchedule: async (context): Promise<CreateScheduleResponseType> => {
                    const { scheduleVersionIdsToCopy } = context.copyRef.getSnapshot().context;

                    const versionsToCopyAsHierarchy: Hierarchy<ScheduleVersion>[] = scheduleVersionIdsToCopy
                        .filter((s) => s.parentId != null)
                        .map((s) => ({ item: s as ScheduleVersion, children: [] }));
                    const schedule: Hierarchy<ScheduleVersion> = { item: context.schedule, children: versionsToCopyAsHierarchy };

                    const res = await scheduleApi.post<Hierarchy<ScheduleVersion>, AxiosResponse<ScheduleVersion>>("/schedule-versions", schedule, {
                        customErrorMessage: {
                            title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.schedule.schedule) }),
                            message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.schedule.schedule) }),
                        },
                    });
                    const scheduleResponse = res.data;

                    return {
                        schedule: scheduleResponse,
                        node: { id: scheduleResponse.id, type: NodeType.ScheduleVersion, parentId: scheduleResponse.scheduleId, authorisation: Authorisation.Admin } as Node, // For permissions all we need is type, id and parent id
                    };
                },
            },
        }
    );
